// src/rds_ui/api/projectsApi.js
//
// Migrated off raw fetch() onto the shared axios instance in
// src/api/client.js so RDS project/SOW calls carry the same auth Bearer
// token, X-User-Email header, and centralized 401 handling as the rest of
// the app (previously these went out completely unauthenticated). Return
// shapes and thrown error messages are kept identical so existing callers
// don't need to change.
import { api } from "../../api/client";

const BASE = "/rds";

// Scoped to one Engagement when an id is given. The engagement is the spine
// of the RDS lifecycle (SPEC-07 D-5): a project belongs to one, and the studio
// lists the projects of whichever engagement is selected in the topbar.
// Omitting it falls back to org-wide, which is what admin views want.
export async function fetchProjects(engagementId) {
  try {
    const params = new URLSearchParams({ limit: "100" });
    if (engagementId) params.set("engagement_id", engagementId);
    const res = await api.get(`${BASE}/projects/?${params.toString()}`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch projects");
  }
}

export async function createProject(data) {
  try {
    const res = await api.post(`${BASE}/projects/`, data);
    return res.data;
  } catch {
    throw new Error("Failed to create project");
  }
}

export async function deleteProject(projectId) {
  try {
    await api.delete(`${BASE}/projects/${projectId}`);
    return true;
  } catch {
    throw new Error("Failed to delete project");
  }
}

export async function uploadSOW(projectId, file) {
  const form = new FormData();
  form.append("file", file);
  try {
    const res = await api.post(`${BASE}/projects/${projectId}/sow/upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch {
    throw new Error("Failed to upload SOW");
  }
}

export async function parseSOW(projectId, sowId) {
  try {
    const res = await api.post(`${BASE}/projects/${projectId}/sow/${sowId}/parse`);
    return res.data;
  } catch {
    throw new Error("Failed to parse SOW");
  }
}

export async function fetchProjectLatestSOW(projectId) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}/sow`);
    if (res.status === 204) return null;
    return res.data;
  } catch (e) {
    if (e?.response?.status === 204) return null;
    throw new Error("Failed to fetch project SOW");
  }
}

export async function fetchSOW(projectId, sowId) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}/sow/${sowId}`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch SOW");
  }
}

export async function fetchSOWSections(projectId, sowId, sectionType) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}/sow/${sowId}/sections`, {
      params: sectionType ? { section_type: sectionType } : undefined,
    });
    return res.data;
  } catch {
    throw new Error("Failed to fetch SOW sections");
  }
}

export async function fetchSOWTables(projectId, sowId) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}/sow/${sowId}/tables`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch SOW tables");
  }
}

export async function fetchProject(projectId) {
  try {
    const res = await api.get(`${BASE}/projects/${projectId}`);
    return res.data;
  } catch {
    throw new Error("Failed to fetch project");
  }
}
